import React from 'react';
import { 
  CheckCircle, 
  Clock, 
  Loader, 
  AlertTriangle, 
  StopCircle, 
  Play,
  Pause,
  XCircle,
  Info,
  Zap
} from 'lucide-react';

/**
 * Unified status configuration for the entire application
 * All status badges should use these consistent definitions
 */
export const STATUS_CONFIG = {
  // === Finding/Ticket Statuses ===
  new: {
    bg: 'bg-info-soft',
    text: 'text-info',
    label: 'New',
    icon: Info
  },
  in_progress: {
    bg: 'bg-warning-soft',
    text: 'text-warning',
    label: 'In Progress',
    icon: Loader,
    animate: true
  },
  resolved: {
    bg: 'bg-success-soft',
    text: 'text-success',
    label: 'Resolved',
    icon: CheckCircle
  },
  false_positive: {
    bg: 'bg-surface-tertiary',
    text: 'text-ink-tertiary',
    label: 'False Positive',
    icon: XCircle
  },
  wont_fix: {
    bg: 'bg-surface-tertiary',
    text: 'text-ink-tertiary',
    label: "Won't Fix",
    icon: XCircle
  },
  reported_fp: {
    bg: 'bg-accent-soft',
    text: 'text-accent',
    label: 'Reported FP',
    icon: AlertTriangle
  },
  
  // === Runner/Execution Statuses ===
  running: {
    bg: 'bg-success-soft',
    text: 'text-success',
    label: 'Running',
    icon: Play,
    dot: true
  },
  starting: {
    bg: 'bg-warning-soft',
    text: 'text-warning',
    label: 'Starting',
    icon: Loader,
    animate: true
  },
  active: {
    bg: 'bg-info-soft',
    text: 'text-info',
    label: 'Active',
    icon: Zap
  },
  paused: {
    bg: 'bg-warning-soft',
    text: 'text-warning',
    label: 'Paused',
    icon: Pause
  },
  stopped: {
    bg: 'bg-surface-tertiary',
    text: 'text-ink-tertiary',
    label: 'Stopped',
    icon: StopCircle
  },
  idle: {
    bg: 'bg-surface-tertiary',
    text: 'text-ink-secondary',
    label: 'Idle',
    icon: Clock
  },
  scheduled: {
    bg: 'bg-warning-soft',
    text: 'text-warning',
    label: 'Scheduled',
    icon: Clock
  },
  
  // === Scan/Task Statuses ===
  pending: {
    bg: 'bg-warning-soft',
    text: 'text-warning',
    label: 'Pending',
    icon: Clock
  },
  completed: {
    bg: 'bg-success-soft',
    text: 'text-success',
    label: 'Completed',
    icon: CheckCircle
  },
  success: {
    bg: 'bg-success-soft',
    text: 'text-success',
    label: 'Success',
    icon: CheckCircle
  },
  failed: {
    bg: 'bg-danger-soft',
    text: 'text-danger',
    label: 'Failed',
    icon: AlertTriangle
  },
  error: {
    bg: 'bg-danger-soft',
    text: 'text-danger',
    label: 'Error',
    icon: AlertTriangle
  },
  cancelled: {
    bg: 'bg-surface-tertiary',
    text: 'text-ink-tertiary',
    label: 'Cancelled',
    icon: StopCircle
  },
  
  // === Priority Levels ===
  critical: {
    bg: 'bg-danger-soft',
    text: 'text-danger',
    label: 'Critical',
    icon: AlertTriangle
  },
  high: {
    bg: 'bg-accent-soft',
    text: 'text-accent',
    label: 'High',
    icon: AlertTriangle
  },
  medium: {
    bg: 'bg-warning-soft',
    text: 'text-warning',
    label: 'Medium',
    icon: Info
  },
  low: {
    bg: 'bg-surface-tertiary',
    text: 'text-ink-secondary',
    label: 'Low',
    icon: Info
  }
};

/**
 * Size configurations for badges
 */
const SIZE_CONFIG = {
  xs: {
    padding: 'px-1.5 py-0.5',
    text: 'text-[10px]',
    iconSize: 'w-3 h-3',
    dotSize: 'w-1 h-1'
  },
  sm: {
    padding: 'px-2 py-0.5',
    text: 'text-tiny',
    iconSize: 'w-3.5 h-3.5',
    dotSize: 'w-1.5 h-1.5'
  },
  md: {
    padding: 'px-2.5 py-1',
    text: 'text-caption',
    iconSize: 'w-4 h-4',
    dotSize: 'w-2 h-2'
  },
  lg: {
    padding: 'px-3 py-1.5',
    text: 'text-body',
    iconSize: 'w-4 h-4',
    dotSize: 'w-2 h-2'
  }
};

/**
 * StatusBadge Component
 * 
 * A unified badge component for displaying status across the application.
 * Supports various sizes, icons, animated dots, and pill/rounded variants.
 * 
 * @param {string} status - The status key (e.g., 'running', 'completed', 'pending')
 * @param {string} label - Optional custom label to override the default
 * @param {string} size - Badge size: 'xs' | 'sm' | 'md' | 'lg'
 * @param {boolean} showDot - Whether to show an animated dot (for 'running' status)
 * @param {boolean} showIcon - Whether to show the status icon
 * @param {boolean} pill - Use pill/rounded-full shape instead of rounded
 * @param {boolean} iconOnly - Show only the icon without label
 * @param {string} className - Additional CSS classes
 */
const StatusBadge = ({ 
  status, 
  label,
  size = 'sm',
  showDot = false,
  showIcon = false,
  pill = false,
  iconOnly = false,
  className = '' 
}) => {
  const normalizedStatus = status?.toLowerCase?.() || 'new';
  const config = STATUS_CONFIG[normalizedStatus] || STATUS_CONFIG.new;
  const sizeConfig = SIZE_CONFIG[size] || SIZE_CONFIG.sm;
  const displayLabel = label || config.label;
  const Icon = config.icon;
  const shouldAnimate = config.animate;
  const shouldShowDot = showDot || config.dot;

  const borderRadius = pill ? 'rounded-full' : 'rounded';

  return (
    <span 
      className={`
        inline-flex items-center gap-1.5 font-medium
        ${config.bg} ${config.text}
        ${sizeConfig.padding} ${sizeConfig.text}
        ${borderRadius}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      {/* Animated dot indicator */}
      {shouldShowDot && (
        <span 
          className={`
            ${sizeConfig.dotSize} rounded-full 
            ${config.text.replace('text-', 'bg-')}
            ${shouldAnimate || normalizedStatus === 'running' ? 'animate-pulse' : ''}
          `} 
        />
      )}
      
      {/* Status icon */}
      {showIcon && Icon && (
        <Icon 
          className={`
            ${sizeConfig.iconSize}
            ${shouldAnimate ? 'animate-spin' : ''}
          `} 
        />
      )}
      
      {/* Label text */}
      {!iconOnly && displayLabel}
    </span>
  );
};

export default StatusBadge;
